import React, { useState, useEffect, lazy, Suspense, startTransition } from "react";
import { MeetingProvider } from "@videosdk.live/react-sdk";
import NavigationBar from "./components/navbar/NavigationBar";

// Lazy-loaded components
const MeetingAppProvider = lazy(() =>
  import("./MeetingAppContextDef").then((module) => ({ default: module.MeetingAppProvider }))
);
const MeetingContainer = lazy(() => import("./meeting/MeetingContainer"));
const LeaveScreen = lazy(() => import("./components/screens/LeaveScreen"));
const JoiningScreen = lazy(() => import("./components/screens/JoiningScreen"));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorInfo: error };
  }

  componentDidCatch(error, info) {
    console.error("Error caught in ErrorBoundary: ", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          backgroundColor: '#131723',
          color: '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: '2rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            backgroundColor: '#1e2538',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid #2d3748',
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            <h1 style={{ 
              fontSize: '1.5rem', 
              marginBottom: '1rem', 
              color: '#f7fafc',
              fontWeight: '600'
            }}>
              Something went wrong
            </h1>
            <p style={{ 
              color: '#a0aec0', 
              fontSize: '0.9rem',
              lineHeight: '1.5'
            }}>
              {this.state.errorInfo ? this.state.errorInfo.toString() : 'An unexpected error occurred'}
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                marginTop: '1.5rem',
                backgroundColor: '#4299e1',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#3182ce'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#4299e1'}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Professional Loading Component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    color: '#a0aec0'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #2d3748',
      borderTop: '3px solid #4299e1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

function App() {
  const [token, setToken] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [micOn, setMicOn] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null);
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);

  const isMobile = window.matchMedia("only screen and (max-width: 768px)").matches;

  // Effect to warn users about page reload on mobile devices
  useEffect(() => {
    if (isMobile) {
      window.onbeforeunload = () => "Are you sure you want to exit?";
    }
  }, [isMobile]);

  // Wake up the server when the app is mounted
  useEffect(() => {
    const wakeUpServer = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_AUTH_URL}/good-morning`, {
          method: "GET",
        });
        console.log(`Server response: ${await res.text()}`);
      } catch (error) {
        console.error("Error waking up the server:", error);
      }
    };
    wakeUpServer();
  }, []);

  // Log SDK state for debugging
  useEffect(() => {
    console.log("Token:", token);
    console.log("Meeting ID:", meetingId);
    console.log("Meeting state: ", { isMeetingStarted, micOn, webcamOn });
  }, [token, meetingId, isMeetingStarted, micOn, webcamOn]);

  // Handle starting the meeting
  const handleStartMeeting = () => {
    startTransition(() => {
      setMeetingStarted(true);
    });
  };

  // Handle leaving the meeting
  const handleLeaveMeeting = () => {
    startTransition(() => {
      setToken("");
      setMeetingId("");
      setParticipantName("");
      setWebcamOn(false);
      setMicOn(false);
      setMeetingStarted(false);
    });
  };

  return (
    <div style={{
      backgroundColor: '#131723',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#ffffff'
    }}>
      <NavigationBar />

      {/* Main Content Container */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        position: "relative",
        paddingTop: isMobile ? "100px" : "120px",
        paddingLeft: isMobile ? "16px" : "24px",
        paddingRight: isMobile ? "16px" : "24px",
        paddingBottom: "24px",
        boxSizing: "border-box",
        zIndex: 1
      }}>
        <ErrorBoundary>
          <MeetingAppProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {/* Meeting Container */}
                {isMeetingStarted ? (
                  <div style={{
                    width: "100%",
                    maxWidth: "1400px",
                    margin: "0 auto",
                    backgroundColor: '#1a1f2e',
                    borderRadius: '16px',
                    border: '1px solid #2d3748',
                    padding: isMobile ? "16px" : "24px",
                    boxSizing: "border-box",
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
                  }}>
                    <MeetingProvider
                      config={{
                        meetingId,
                        micEnabled: micOn,
                        webcamEnabled: webcamOn,
                        name: participantName || "TestUser",
                        multiStream: true,
                        customCameraVideoTrack: customVideoStream,
                        customMicrophoneAudioTrack: customAudioStream,
                      }}
                      token={token}
                      reinitialiseMeetingOnConfigChange={true}
                      joinWithoutUserInteraction={true}
                    >
                      <MeetingContainer
                        onMeetingLeave={handleLeaveMeeting}
                        setIsMeetingLeft={setIsMeetingLeft}
                      />
                    </MeetingProvider>
                  </div>
                ) : isMeetingLeft ? (
                  <div style={{
                    width: "100%",
                    maxWidth: "600px",
                    margin: "0 auto",
                    backgroundColor: '#1a1f2e',
                    borderRadius: '16px',
                    border: '1px solid #2d3748',
                    padding: "32px",
                    boxSizing: "border-box",
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
                  }}>
                    <LeaveScreen setIsMeetingLeft={setIsMeetingLeft} />
                  </div>
                ) : (
                 
                    <JoiningScreen
                      participantName={participantName}
                      setParticipantName={setParticipantName}
                      setMeetingId={setMeetingId}
                      setToken={setToken}
                      micOn={micOn}
                      setMicOn={setMicOn}
                      webcamOn={webcamOn}
                      setWebcamOn={setWebcamOn}
                      customAudioStream={customAudioStream}
                      setCustomAudioStream={setCustomAudioStream}
                      customVideoStream={customVideoStream}
                      setCustomVideoStream={setCustomVideoStream}
                      onClickStartMeeting={handleStartMeeting}
                      startMeeting={isMeetingStarted}
                      setIsMeetingLeft={setIsMeetingLeft}
                    />
                
                )}
              </div>
            </Suspense>
          </MeetingAppProvider>

          {/* Professional Footer Accent */}
          <div style={{
            marginTop: "auto",
            paddingTop: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <div style={{
              width: "100%",
              maxWidth: "400px",
              height: "3px",
              background: "linear-gradient(90deg, #4299e1 0%, #63b3ed 50%, #90cdf4 100%)",
              borderRadius: "2px",
              opacity: "0.6"
            }} />
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
}

export default App;